import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
interface SubItem {
  name: string;
  path: string;
}
interface NavItem {
  name: string;
  path: string;
  color: string;
  hoverColor: string;
  bgColor: string;
  subItems: SubItem[];
}
export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);
  const navItems: NavItem[] = [{
    name: 'Fat Liberation',
    path: '/fat-liberation',
    color: 'text-brand-magenta',
    hoverColor: 'hover:text-brand-magenta',
    bgColor: 'bg-brand-magenta',
    subItems: [{
      name: 'Systems, Power & Bias',
      path: '/clusters/systems-power'
    }, {
      name: 'Commitments & Principles',
      path: '/clusters/commitments'
    }, {
      name: 'Terminology & Glossary',
      path: '/clusters/terminology'
    }, {
      name: 'Fat Frameworks',
      path: '/clusters/fat-frameworks'
    }]
  }, {
    name: 'Learning & Growth',
    path: '/learning-growth',
    color: 'text-brand-cyan',
    hoverColor: 'hover:text-brand-cyan',
    bgColor: 'bg-brand-cyan',
    subItems: [{
      name: 'Courses & Experiences',
      path: '/clusters/courses'
    }, {
      name: 'Ways to Learn',
      path: '/clusters/ways-to-learn'
    }]
  }, {
    name: 'The Publication',
    path: '/publication',
    color: 'text-brand-orange',
    hoverColor: 'hover:text-brand-orange',
    bgColor: 'bg-brand-orange',
    subItems: [{
      name: 'About the Publication',
      path: '/clusters/about-publication'
    }, {
      name: 'Contribute',
      path: '/clusters/contribute'
    }, {
      name: 'Read Waestm',
      path: '/clusters/read-waestm'
    }, {
      name: 'Our Contributors',
      path: '/clusters/contributors'
    }]
  }, {
    name: 'Blog',
    path: '/blog',
    color: 'text-brand-orange',
    hoverColor: 'hover:text-brand-orange',
    bgColor: 'bg-brand-orange',
    subItems: [{
      name: 'All Posts',
      path: '/blog'
    }, {
      name: 'Browse & Search',
      path: '/blog/catalogue'
    }, {
      name: "Founders' Q&A",
      path: '/blog/why-we-built-regrowth'
    }]
  }, {
    name: 'About',
    path: '/about',
    color: 'text-brand-lime',
    hoverColor: 'hover:text-brand-lime',
    bgColor: 'bg-brand-lime',
    subItems: [{
      name: 'Who We Are',
      path: '/clusters/who-we-are'
    }, {
      name: 'How We Work',
      path: '/clusters/how-we-work'
    }, {
      name: 'Co-Production Principles',
      path: '/clusters/co-production'
    }]
  }];
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg py-2' : 'bg-white border-b-2 border-gray-100 py-3'}`} data-id="element-126">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-127">
        <div className="flex justify-between items-center" data-id="element-128">
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-id="element-129">
            <span className="text-2xl font-black tracking-tighter text-brand-black group-hover:text-brand-magenta transition-colors" data-id="element-130">
              RE<span className="text-brand-magenta" data-id="element-131">GROWTH</span>
            </span>
          </Link>

          {/* Desktop Navigation - Changed from lg:flex to md:flex for better visibility */}
          <nav className="hidden md:flex items-center space-x-1" data-id="element-132">
            {navItems.map(item => <div key={item.name} className="relative" onMouseEnter={() => setActiveDropdown(item.name)} onMouseLeave={() => setActiveDropdown(null)} data-id="element-133">
                <Link to={item.path} className={`flex items-center px-3 lg:px-4 py-2 text-xs lg:text-sm font-bold uppercase tracking-wide text-gray-700 ${item.hoverColor} transition-colors rounded-sm`} data-id="element-134">
                  {item.name}
                  <ChevronDown className="ml-1 h-3 w-3 opacity-50" data-id="element-135" />
                </Link>

                {/* Dropdown */}
                <AnimatePresence data-id="element-136">
                  {activeDropdown === item.name && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="absolute left-0 mt-1 w-64 bg-white border-2 border-brand-black shadow-2xl rounded-sm overflow-hidden" data-id="element-137">
                      <div className={`h-1 ${item.bgColor}`} data-id="element-138"></div>
                      {item.subItems.map(sub => <Link key={sub.name} to={sub.path} className={`block px-5 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 ${item.hoverColor} transition-colors border-b border-gray-100 last:border-b-0`} data-id="element-139">
                          {sub.name}
                        </Link>)}
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </nav>

          {/* CTA - Changed from lg:block to md:block */}
          <div className="hidden md:block" data-id="element-140">
            <Button size="sm" color="magenta" data-id="element-141">
              Sign In
            </Button>
          </div>

          {/* Mobile Menu Button - Changed from lg:hidden to md:hidden */}
          <button className="md:hidden p-2 text-gray-600 hover:text-brand-black transition-colors" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu" data-id="element-142">
            {isOpen ? <X className="h-6 w-6" data-id="element-143" /> : <Menu className="h-6 w-6" data-id="element-144" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - Changed from lg:hidden to md:hidden */}
      <AnimatePresence data-id="element-145">
        {isOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3
      }} className="md:hidden bg-white border-t-2 border-gray-100 overflow-hidden" data-id="element-146">
            <div className="px-4 pt-4 pb-6 space-y-2 max-h-[70vh] overflow-y-auto" data-id="element-147">
              {navItems.map(item => <div key={item.name} className="py-2" data-id="element-148">
                  <Link to={item.path} className={`block py-2 text-base font-bold uppercase ${item.color} flex items-center justify-between`} onClick={() => setIsOpen(false)} data-id="element-149">
                    {item.name}
                    <div className={`w-8 h-1 ${item.bgColor} rounded-full`} data-id="element-150"></div>
                  </Link>
                  <div className="pl-4 mt-2 space-y-2 border-l-2 border-gray-100 ml-1" data-id="element-151">
                    {item.subItems.map(sub => <Link key={sub.name} to={sub.path} className="block text-sm font-medium text-gray-600 py-1.5 hover:text-brand-black transition-colors" onClick={() => setIsOpen(false)} data-id="element-152">
                        {sub.name}
                      </Link>)}
                  </div>
                </div>)}
              <div className="pt-4 mt-4 border-t-2 border-gray-100" data-id="element-153">
                <Button className="w-full" color="magenta" data-id="element-154">
                  Sign In
                </Button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}